import { useNavigate } from 'react-router-dom';
import { useProgress } from '@/context/ProgressContext';
import { getAllItems } from '@/data';
import XPRing from '@/components/XPRing';
import { Rocket, Map, Star, Zap, BookOpen, Trophy, RotateCcw } from 'lucide-react';

const BADGES = [
  { id: 'explorer', name: 'Explorer', desc: '25% done', icon: Star, color: '#1E90FF' },
  { id: 'builder', name: 'Builder', desc: '50% done', icon: Zap, color: '#16FF00' },
  { id: 'creator', name: 'Creator', desc: '75% done', icon: Trophy, color: '#FF5E00' },
  { id: 'master', name: 'Master', desc: '100% done', icon: Rocket, color: '#FF0099' },
];

const MAX_XP = 4000;

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="bg-[#121225]/80 border border-white/10 rounded-xl p-4 text-center backdrop-blur-sm">
    <Icon size={18} className="mx-auto text-[#A1A1AA] mb-1" />
    <div className="text-xl font-bold font-outfit text-white">{value}</div>
    <div className="text-xs text-[#71717A] font-quicksand">{label}</div>
  </div>
);

export default function Dashboard() {
  const navigate = useNavigate();
  const { progress, isUnlocked, totalItems, resetProgress } = useProgress();
  const items = getAllItems();

  const nextItem = items.find(
    item => !progress.completedItems.includes(item.id) && isUnlocked(item.id)
  );

  const completedCount = progress.completedItems.length;
  const percentage = Math.round((completedCount / totalItems) * 100);

  return (
    <div className="relative z-10 min-h-screen flex flex-col items-center px-4 py-8" data-testid="dashboard">
      {/* Title */}
      <div className="text-center mb-8 fade-in-up">
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold font-outfit tracking-tight text-white glow-pink">
          Code <span className="text-[#FF0099]">Galaxy</span>
        </h1>
        <p className="text-[#A1A1AA] mt-2 font-quicksand text-base md:text-lg">
          Explore the Universe of Code
        </p>
      </div>

      {/* XP Ring */}
      <div className="fade-in-up" style={{ animationDelay: '0.1s' }}>
        <XPRing current={progress.totalXP} max={MAX_XP} size={180} />
      </div>

      {/* Progress text */}
      <div className="mt-4 text-center fade-in-up" style={{ animationDelay: '0.15s' }}>
        <p className="text-sm text-[#A1A1AA] font-quicksand">
          {completedCount} of {totalItems} lessons done ({percentage}%)
        </p>
      </div>

      {/* Badges */}
      <div className="flex gap-3 sm:gap-4 mt-8 fade-in-up" style={{ animationDelay: '0.2s' }}>
        {BADGES.map(badge => {
          const unlocked = progress.badges.includes(badge.id);
          const Icon = badge.icon;
          return (
            <div
              key={badge.id}
              className={`flex flex-col items-center gap-1 p-3 rounded-xl border transition-all ${
                unlocked
                  ? 'border-white/20 bg-white/5'
                  : 'border-white/5 bg-white/[0.02] opacity-40 grayscale'
              }`}
              style={unlocked ? { boxShadow: `0 0 15px ${badge.color}25` } : {}}
              data-testid={`badge-${badge.id}`}
            >
              <Icon size={24} style={{ color: unlocked ? badge.color : '#71717A' }} />
              <span
                className="text-xs font-bold font-outfit"
                style={{ color: unlocked ? badge.color : '#71717A' }}
              >
                {badge.name}
              </span>
            </div>
          );
        })}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 mt-8 w-full max-w-sm fade-in-up" style={{ animationDelay: '0.3s' }}>
        <StatCard icon={BookOpen} label="Done" value={completedCount} />
        <StatCard icon={Zap} label="XP" value={progress.totalXP} />
        <StatCard icon={Trophy} label="Badges" value={progress.badges.length} />
      </div>

      {/* Continue Button */}
      {nextItem && (
        <button
          onClick={() => navigate(`/lesson/${nextItem.id}`)}
          className="mt-8 bg-[#FF0099] hover:bg-[#D60080] text-white rounded-full px-8 py-4 font-bold font-outfit text-lg shadow-[0_0_20px_rgba(255,0,153,0.3)] transition-all hover:scale-105 active:scale-95 fade-in-up flex items-center gap-3"
          style={{ animationDelay: '0.4s' }}
          data-testid="continue-btn"
        >
          <Rocket size={20} />
          {completedCount === 0 ? 'Start Your Journey' : 'Continue Learning'}
        </button>
      )}

      {completedCount === totalItems && (
        <div className="mt-8 text-center fade-in-up" style={{ animationDelay: '0.4s' }}>
          <h2 className="text-2xl font-bold font-outfit text-[#FFED00] glow-blue">
            You completed Code Galaxy!
          </h2>
          <p className="text-[#A1A1AA] mt-2 font-quicksand">Your bhai is SO proud of you!</p>
        </div>
      )}

      {/* Course Map */}
      <button
        onClick={() => navigate('/map')}
        className="mt-6 bg-transparent border-2 border-[#1E90FF] text-[#1E90FF] hover:bg-[#1E90FF] hover:text-white rounded-full px-6 py-2 font-bold font-outfit transition-all flex items-center gap-2"
        data-testid="course-map-btn"
      >
        <Map size={16} /> Course Map
      </button>

      {/* Reset */}
      {completedCount > 0 && (
        <button
          onClick={() => {
            if (window.confirm('Reset all progress? This cannot be undone.')) resetProgress();
          }}
          className="mt-8 text-[#71717A] hover:text-[#FF0055] text-xs flex items-center gap-1 transition-colors"
          data-testid="reset-progress-btn"
        >
          <RotateCcw size={12} /> Reset Progress
        </button>
      )}
    </div>
  );
}
